/**
 * Creates a comparator function for sorting arrays of objects by one or more attributes.
 * 
 * Features:
 * - Sorts by multiple attributes in order of priority (first attribute is primary sort key)
 * - Case-insensitive string comparison using toLowerCase()
 * - Null and undefined values are sorted to the end
 * - Throws error if comparing different data types (except null/undefined)
 * - Uses stable sorting (maintains relative order for equal elements)
 * 
 * @param {...string} attributes - The object property names to sort by, in order of priority
 * @returns {Function} A comparator function suitable for Array.sort()
 * 
 * @throws {Error} When comparing values of different types (excluding null/undefined)
 * 
 * @example
 * // Sort by single attribute
 * const users = [{name: "Bob"}, {name: "Alice"}]
 * users.sort(AttributeSort("name"))
 * // Result: [{name: "Alice"}, {name: "Bob"}]
 * 
 * @example
 * // Sort by multiple attributes
 * const data = [
 *   {dept: "Engineering", name: "Charlie"},
 *   {dept: "Engineering", name: "Alice"},
 *   {dept: "Marketing", name: "Bob"}
 * ]
 * data.sort(AttributeSort("dept", "name"))
 * // Result: Engineering dept sorted by name, then Marketing dept
 * 
 * @example
 * // Real-world usage: Operating hours sorting
 * operatingHours.sort(AttributeSort("day_of_week", "start_time"))
 */
export const AttributeSort = (...attributes) => {
  const sortHandler = (a, b, attributeList) => {
    const attribute = attributeList.shift()

    let core1 = a[attribute]
    let core2 = b[attribute]

    // Handle null/undefined values first - put them at the end
    if (core1 == null && core2 == null) return 0
    if (core1 == null && core2 != null) return 1
    if (core1 != null && core2 == null) return -1

    // Convert strings to lowercase for case-insensitive comparison
    if (typeof core1 === "string") core1 = core1.toLocaleLowerCase()
    if (typeof core2 === "string") core2 = core2.toLocaleLowerCase()
    
    // Check for type mismatch (null/undefined already handled above)
    const type1 = typeof core1
    const type2 = typeof core2
    if (type1 !== type2) {
      throw new Error(`Type mismatch: cannot compare ${type1} with ${type2}`)
    }
    
    // Normal comparison for non-null values of the same type
    if (core1 > core2) return 1
    if (core1 < core2) return -1

    if (attributeList.length >= 1) {
      return sortHandler(a, b, attributeList)
    }

    return 0
  }

  return function (a, b) {
    return sortHandler(a, b, attributes.slice())
  }
}